/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

// Google Identity Services is loaded as a script tag on demand, not an npm
// dependency: the bundle stays clean and the button only costs a request on
// the login page, and only when a client id is configured.

const GSI_SRC = "https://accounts.google.com/gsi/client";

export const googleClientId: string =
  (process.env.PUBLIC_GOOGLE_CLIENT_ID as string | undefined) ?? "";

type CredentialResponse = { credential: string };

type GoogleId = {
  initialize: (config: {
    client_id: string;
    callback: (response: CredentialResponse) => void;
  }) => void;
  renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
};

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleId } };
  }
}

let loader: Promise<GoogleId> | null = null;

function loadGoogleId(): Promise<GoogleId> {
  loader ??= new Promise((resolve, reject) => {
    const loaded = window.google?.accounts?.id;
    if (loaded !== undefined) {
      resolve(loaded);
      return;
    }
    const script = document.createElement("script");
    script.src = GSI_SRC;
    script.async = true;
    script.onload = () => {
      const id = window.google?.accounts?.id;
      if (id === undefined) {
        reject(new Error("Google sign-in could not be loaded."));
      } else {
        resolve(id);
      }
    };
    script.onerror = () => {
      loader = null;
      reject(new Error("Google sign-in could not be loaded."));
    };
    document.head.appendChild(script);
  });
  return loader;
}

export async function renderGoogleButton(
  container: HTMLElement,
  onCredential: (credential: string) => void,
): Promise<void> {
  const id = await loadGoogleId();
  id.initialize({
    client_id: googleClientId,
    callback: (response) => onCredential(response.credential),
  });
  id.renderButton(container, {
    theme: "outline",
    size: "large",
    text: "signin_with",
    shape: "pill",
    width: Math.min(Math.max(container.clientWidth, 220), 400),
  });
}
